import React, { type ReactNode, type ErrorInfo } from 'react'
import { Logger } from '@egw/logger'

const log = new Logger('CopilotErrorBoundary')

interface CopilotErrorBoundaryProps {
  children: ReactNode
  /** Fallback content to render when CopilotKit fails - should be the app content without CopilotKit */
  fallback: ReactNode
}

interface CopilotErrorBoundaryState {
  hasError: boolean
  error?: Error
}

/**
 * Error boundary specifically for CopilotKit integration.
 * Gracefully handles CopilotKit failures (e.g., backend not available in mock mode)
 * by rendering the fallback (app content) without CopilotKit wrapper.
 */
export class CopilotErrorBoundary extends React.Component<
  CopilotErrorBoundaryProps,
  CopilotErrorBoundaryState
> {
  constructor(props: CopilotErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): CopilotErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error but don't crash the app
    console.warn('[CopilotErrorBoundary] Error caught, app will continue without AI chat:', error.message)
    log.warn('CopilotKit error caught (app will continue without AI chat):', error.message)
    log.debug('Error details:', errorInfo)
  }

  render() {
    if (this.state.hasError) {
      // Render the fallback (app content) without CopilotKit wrapper
      // This allows the app to function normally when CopilotKit fails
      console.log('[CopilotErrorBoundary] CopilotKit disabled, rendering app without AI chat')
      return this.props.fallback
    }

    return this.props.children
  }
}

